import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { FC, ReactNode } from 'react';

export interface ReportChartEnlargeDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  /** e.g. export — shown in the title bar before Close */
  headerActions?: ReactNode;
  children: ReactNode;
}

/**
 * Modal shell for viewing a report chart at a larger size (same data as the card).
 */
const ReportChartEnlargeDialog: FC<ReportChartEnlargeDialogProps> = ({ open, title, onClose, headerActions, children }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="report-chart-enlarge-title"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 'min(96vw, 1240px)' },
          height: { xs: '100%', sm: 'min(92vh, 900px)' },
          maxHeight: '100%',
          m: { xs: 0, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: 0, sm: 2 },
        },
      }}>
      <DialogTitle
        id="report-chart-enlarge-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 1,
          py: 1.25,
          flexShrink: 0,
        }}>
        <Typography
          component="span"
          variant="inherit"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 700,
            fontSize: 16,
          }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          {headerActions}
          <IconButton aria-label="Close enlarged chart" onClick={onClose} size="small" edge="end">
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          p: { xs: 1.5, sm: 2 },
          overflow: 'hidden',
        }}>
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default ReportChartEnlargeDialog;
